// Simplified POTable.jsx
import React, { useState, useMemo } from "react";
import { RotaButton } from "@teamrota/rota-design";
import {
  TableWrapper,
  StyledTable,
  TableHead,
  TableHeader,
  ButtonContainer,
  ActionButton,
  TableContainer,
  SortArrow
} from "../po-numbers.styles";
import { COLUMNS, COLUMN_WIDTHS } from "../consts";
import { getSubvenueOptions } from "../utils";
import { usePOTable } from "../hooks/use-po-table";
import POTableRow from "./po-table-row";
import asyncConfirm from "~/src/utils/async-confirm";
import SkeletonRows from "./po-table-skeleton-loader";

const POTable = ({
  initialPONumbers = [],
  venues,
  roleRates,
  onSaveChanges,
  targetAccountId,
  isLoading
}) => {
  // Add sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending"
  });

  const {
    poData,
    hasChanges,
    isValidating,
    rowsBeingValidated,
    rowHasErrors,
    hasValidationErrors,
    handleAddRow,
    handleRowUpdate,
    handleDeleteRow,
    handleCancel,
    handleSave
  } = usePOTable(initialPONumbers, targetAccountId);

  // Prepare options for dropdowns
  const venueOptions = [
    { value: null, label: "---" },
    ...(venues?.map(({ id, name }) => ({ value: id, label: name })) || [])
  ];

  const roleRateOptions = [
    { value: null, label: "---" },
    ...(roleRates?.map(({ id, role }) => ({ value: id, label: role.name })) ||
      [])
  ];

  // Handle sort request
  const requestSort = key => {
    setSortConfig(prevConfig => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "ascending"
          ? "descending"
          : "ascending"
    }));
  };

  // Get sort indicator for a column
  const getSortArrow = key => {
    if (sortConfig.key !== key) return null;
    return <SortArrow direction={sortConfig.direction} />;
  };

  // Helper functions for data values
  const getValueFunctions = {
    poNumber: row => row.purchaseOrderNumber || "",
    venue: row => {
      const venue = venues?.find(v => v.id === row.venueId);
      return venue ? venue.name : "";
    },
    subvenue: row => {
      for (const venue of venues || []) {
        const subvenue = venue.subvenues?.find(s => s.id === row.subvenueId);
        if (subvenue) return subvenue.name;
      }
      return "";
    },
    role: row => {
      const roleRate = roleRates?.find(r => r.id === row.roleRateId);
      return roleRate ? roleRate.role.name : "";
    },
    startDate: row => (row.startDate ? new Date(row.startDate) : new Date(0)),
    endDate: row => (row.endDate ? new Date(row.endDate) : new Date(0)),
    status: row => row.status || ""
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return poData;

    return [...poData].sort((a, b) => {
      const getValue = getValueFunctions[sortConfig.key];
      if (!getValue) return 0;

      const aValue = getValue(a);
      const bValue = getValue(b);

      // Handle different data types
      if (typeof aValue === "string") {
        return sortConfig.direction === "ascending"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (aValue instanceof Date) {
        return sortConfig.direction === "ascending"
          ? aValue - bValue
          : bValue - aValue;
      }

      // Default numeric comparison
      return sortConfig.direction === "ascending"
        ? aValue - bValue
        : bValue - aValue;
    });
  }, [poData, sortConfig, venues, roleRates]);

  // Row operations
  const onRowUpdate = (id, field, value) => handleRowUpdate(id, field, value);

  const onRowDelete = async id => {
    if (
      await asyncConfirm("Do you want to delete this PO number?", {
        shouldHideSubText: true,
        shouldAddPadding: true,
        confirmButtonText: "Delete",
        falseButtonText: "Cancel"
      })
    ) {
      handleDeleteRow(id);
    }
  };

  const renderTableBody = () => {
    if (isLoading) {
      return <SkeletonRows rowCount={3} />;
    }

    if (sortedData.length > 0) {
      return sortedData.map(row => (
        <POTableRow
          key={row.id}
          row={row}
          columnWidths={COLUMN_WIDTHS}
          venueOptions={venueOptions}
          subvenueOptions={getSubvenueOptions(row.venueId, venues)}
          roleRateOptions={roleRateOptions}
          isBeingValidated={!!rowsBeingValidated[row.id]}
          hasError={rowHasErrors(row.id)}
          onUpdate={onRowUpdate}
          onDelete={onRowDelete}
        />
      ));
    }

    // No data case
    return (
      <tr>
        <td colSpan="8" style={{ textAlign: "center", padding: "1.5rem" }}>
          No PO numbers found. Use the "Add" button below to create a new PO
          number.
        </td>
      </tr>
    );
  };

  return (
    <TableContainer>
      <TableWrapper>
        <StyledTable>
          <TableHead>
            <tr>
              {COLUMNS.map(column => (
                <TableHeader
                  key={column.key}
                  width={column.width}
                  onClick={
                    column.sortable ? () => requestSort(column.key) : undefined
                  }
                  className={column.sortable ? "sortable-header" : ""}
                >
                  {column.label} {column.sortable && getSortArrow(column.key)}
                </TableHeader>
              ))}
            </tr>
          </TableHead>
          <tbody>{renderTableBody()}</tbody>
        </StyledTable>
      </TableWrapper>
      <ButtonContainer>
        <RotaButton
          onClick={handleAddRow}
          style={{ marginRight: "auto" }}
          variant="outlined"
          disabled={isValidating || Object.keys(rowsBeingValidated).length > 0}
        >
          Add {poData.length > 0 ? "another" : ""}
        </RotaButton>

        {hasChanges && (
          <>
            <ActionButton
              onClick={handleCancel}
              variant="outlined"
              disabled={
                isValidating || Object.keys(rowsBeingValidated).length > 0
              }
            >
              Cancel
            </ActionButton>
            <ActionButton
              onClick={() => handleSave(onSaveChanges)}
              disabled={
                isValidating ||
                Object.keys(rowsBeingValidated).length > 0 ||
                hasValidationErrors()
              }
            >
              {isValidating ? "Validating..." : "Save"}
            </ActionButton>
          </>
        )}
      </ButtonContainer>
    </TableContainer>
  );
};

export default POTable;
